import { Link } from 'react-router-dom';
import { useEffect } from 'react';

const sections = [
  {
    title: '2. Eligibility for Refund',
    content: `Refunds may be granted under the following conditions:<br/>A payment was made directly through the Company (e.g., via the Site using a valid payment method)<br/>A verified issue occurred that materially affected the delivery of the service<br/>The refund request is submitted within 14 days of the original payment date<br/>Refunds are not guaranteed and are assessed on a case-by-case basis.<br/>Refunds will generally not be granted in the following situations:<br/>Change of mind or general dissatisfaction<br/>Failure to use the service<br/>User error or misunderstanding of the service<br/>Issues caused by third-party services or external factors beyond our control<br/>Requests submitted after the applicable refund period`,
  },
  {
    title: '3. Refund Request Process',
    content: `To request a refund, you must contact our support team:<br/>Email: support@regent-digital.com<br/>Phone: +447520657571<br/>Your request should include:<br/>Full name and contact details<br/>Proof of payment (date, amount, transaction reference if available)<br/>Description of the issue or reason for the request<br/>We may request additional information to verify your claim.`,
  },
  {
    title: '4. Review and Approval',
    content: `All refund requests are reviewed individually.<br/>The Company reserves the right to:<br/>Approve or decline a refund request<br/>Request additional documentation<br/>Determine whether the issue qualifies under this Policy`,
  },
  {
    title: '5. Processing of Refunds',
    content: `If a refund is approved:<br/>The refund will be issued using the original payment method where possible<br/>Processing time may take 5–14 business days, depending on the payment provider<br/>Any applicable fees or taxes may be included where required by law`,
  },
  {
    title: '6. Cancellations',
    content: `If the Company offers services that can be cancelled:<br/>Cancellation prevents future charges (if applicable)<br/>Cancellation does not automatically entitle the user to a refund<br/>Refunds remain subject to the conditions outlined in this Policy.`,
  },
  {
    title: '7. Limitation of Liability',
    content: `The Company is not responsible for:<br/>Issues caused by user devices, software, or internet connectivity<br/>Interruptions caused by third-party services<br/>Factors outside the Company’s reasonable control`,
  },
  {
    title: '8. Changes to this Policy',
    content: `We reserve the right to modify this Refund Policy at any time.<br/>Updated versions will be posted on this page with a revised effective date. Continued use of the Site constitutes acceptance of the updated Policy.`,
  },
  {
    title: '9. Contact Information',
    content: `Regent Digital LTD<br/>International House<br/>55 Longsmith Street<br/>Gloucester<br/>GL1 2HT<br/>United Kingdom<br/>Email: support@regent-digital.com<br/>Phone: +447520657571`,
  },
];

export default function RefundPolicy() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#1A1A1A] border-b border-white/10">
        <div className="px-8 md:px-16 py-4 flex items-center justify-between">
          <Link to="/" className="cursor-pointer flex items-center gap-3">
            <div className="relative w-10 h-10 flex items-center justify-center flex-shrink-0">
              <div className="absolute inset-0 bg-[#FFD93D] rounded-xl rotate-3" />
              <div className="absolute inset-0 bg-black rounded-xl -rotate-1" />
              <span
                className="relative z-10 font-black text-[#FFD93D]"
                style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '18px', letterSpacing: '-0.05em' }}
              >RD</span>
            </div>
            <div className="flex flex-col leading-none">
              <span
                className="text-white font-black uppercase"
                style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '0.08em' }}
              >REGENT</span>
              <span
                className="text-[#FFD93D] font-black uppercase"
                style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '0.08em' }}
              >DIGITAL</span>
            </div>
          </Link>

          <Link
            to="/"
            className="flex items-center gap-2 text-white/60 hover:text-[#FFD93D] transition-colors font-semibold text-sm cursor-pointer whitespace-nowrap"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </div>
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <div className="pt-32 pb-16 px-8 md:px-16 border-b border-white/10">
        <div className="max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1.5 h-8 bg-[#FFD93D] rounded-full" />
            <span
              className="text-[#FFD93D] font-black uppercase tracking-widest text-sm"
              style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.2em' }}
            >Legal</span>
          </div>
          <h1
            className="text-white font-black uppercase mb-4"
            style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(48px, 7vw, 96px)', lineHeight: 0.9, letterSpacing: '-0.02em' }}
          >
            Refund Policy
          </h1>
          <p className="text-white/40 font-semibold text-sm mt-6">
            Last Updated: April 14, 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="px-8 md:px-16 py-16">
        <div className="max-w-4xl">
          {/* Intro */}
          <p className="text-white/60 font-semibold text-base leading-relaxed mb-12 border-l-2 border-[#FFD93D] pl-6" dangerouslySetInnerHTML={{__html: `This Refund Policy outlines the conditions under which Regent Digital LTD ("Company", "we", "our", or "us") may provide refunds for payments made for services offered through this website (the "Site").<br/><br/>We aim to provide high-quality services and fair treatment to all users. Refunds are considered in accordance with the terms set out below.`}} />

          {/* Sections */}
          <div className="space-y-10">
            {sections.map((section, index) => (
              <div
                key={index}
                className="group border border-white/10 rounded-xl p-8 hover:border-[#FFD93D]/30 transition-colors bg-[#1A1A1A]/50"
              >
                <h2
                  className="text-white font-black uppercase mb-4"
                  style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '0.05em' }}
                >
                  {section.title}
                </h2>
                <p className="text-white/55 font-semibold text-sm leading-relaxed" dangerouslySetInnerHTML={{__html: section.content}} />
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-16 border-t border-white/10 pt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <p className="text-white/70 font-semibold text-base mb-1">
                Questions about our Refund Policy?
              </p>
              <p className="text-white/40 font-semibold text-sm">
                Our team is happy to help clarify anything.
              </p>
            </div>
            <a
              href="mailto:support@regent-digital.com"
              className="bg-[#FFD93D] text-black font-black uppercase px-8 py-3 rounded-full hover:bg-[#FF8C42] hover:text-white transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
              style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '16px' }}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-mail-line" />
              </div>
              Contact Us
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] py-8 px-8 md:px-16 border-t border-white/10 mt-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/40 font-semibold text-sm">
            © 2026 Regent Digital LTD. All rights reserved. Registered in England &amp; Wales.
          </p>
          <Link
            to="/"
            className="text-white/40 hover:text-[#FFD93D] font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            ← Back to Homepage
          </Link>
        </div>
      </footer>
    </div>
  );
}
